import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { EntityFormDialog } from '../../components/dialogs/EntityFormDialog';
import { useAlert } from '../../components/dialogs/DialogProvider';
import { selectableSites, useCreateIssue, useEditIssue, useMe, useSitesList } from '../../api/queries';
import type { Issue } from '../../api/types';
import { COMPONENT_AREAS, findArea, findSubArea } from './componentAreas';
import { parseIssueBody, type ExistingAttachment } from './issueBodyParse';
import {
  categoryFromLabels, priorityFromLabels, statusFromLabels,
} from './StatusChip';

const CATEGORIES = ['Bug', 'Feature', 'Regulatory', 'Hardware', 'Urgent'] as const;
const PRIORITIES = ['P1-Critical', 'P2-High', 'P3-Medium', 'P4-Low'] as const;
const STATUSES   = [
  'raised', 'verified', 'approved-for-development',
  'planned', 'ready-for-development', 'in-review', 'completed',
] as const;
const ACCEPT     = 'image/jpeg,image/png,video/mp4';
const MAX_FILES  = 5;
const MAX_BYTES  = 10 * 1024 * 1024;

const AREA_CODES = COMPONENT_AREAS.map(a => a.code) as [string, ...string[]];

const schema = z.object({
  title:            z.string().trim().min(1, 'Required').max(200, 'At most 200 characters'),
  category:         z.enum(CATEGORIES, { errorMap: () => ({ message: 'Pick a category' }) }),
  priority:         z.enum(PRIORITIES, { errorMap: () => ({ message: 'Pick a priority' }) }),
  // Component area is REQUIRED — every issue must be attributable to a
  // software area so triage can route it. Sub-area is optional and only
  // applicable when the chosen area has one (see componentAreas.ts).
  area:             z.enum(AREA_CODES, { errorMap: () => ({ message: 'Pick a component area' }) }),
  subArea:          z.string().optional().or(z.literal('').transform(() => undefined)),
  // Edit-only — pipeline status. Unused (undefined) in create mode;
  // server stamps 'status:raised' on every new issue.
  status:           z.enum(STATUSES).optional(),
  // Optional — issues can be raised without a specific site.
  siteId:           z.string().optional().or(z.literal('').transform(() => undefined)),
  // Optional — issues can be raised without a specific machine.
  machineSerial:    z.string().trim().max(40).optional()
                      .or(z.literal('').transform(() => undefined)),
  description:      z.string().trim().min(20, 'At least 20 characters').max(8000),
  stepsToReproduce: z.string().trim().max(8000).optional()
                      .or(z.literal('').transform(() => undefined)),
});

export type IssueFormValues = z.infer<typeof schema>;

export type IssueFormDialogProps = {
  open: boolean;
  onClose: () => void;
} & (
  | { mode: 'create'; issue?: undefined }
  | { mode: 'edit'; issue: Issue }
);

/**
 * "Raise issue" / "Edit issue" form. One component, two modes —
 * `create` POSTs to /api/v1/issues, `edit` PATCHes /api/v1/issues/{n}
 * after pre-populating from the existing issue's body + labels.
 *
 * Uses EntityFormDialog (PORTAL_FORM_RULES §4) for the dialog chrome
 * + react-hook-form integration. New file attachments are held in
 * component state and added to the multipart body at submit time —
 * react-hook-form doesn't handle multi-file inputs ergonomically.
 *
 * In edit mode, existing attachments are shown alongside the
 * "add new" button with a remove (X) on each. The submit sends the
 * URLs of the attachments the user chose to KEEP — anything not
 * listed is dropped from the regenerated body server-side.
 */
export function IssueFormDialog(props: IssueFormDialogProps) {
  const { open, onClose, mode } = props;
  const create = useCreateIssue();
  const edit   = useEditIssue();
  const [files, setFiles] = useState<File[]>([]);

  // Existing attachments + user's keep/remove selection (edit mode only).
  // Stored as full URL → boolean (true = keep, false = pending removal).
  const [keepMap, setKeepMap] = useState<Map<string, boolean>>(new Map());

  const initial = useMemo<IssueFormValues>(() => {
    if (mode === 'edit') {
      const parsed = parseIssueBody(props.issue.body);
      const cat = titleCaseCategory(categoryFromLabels(props.issue.labels) ?? 'bug');
      const prio = formatPriority(priorityFromLabels(props.issue.labels) ?? 'p3-medium');
      const areaCode = parsed.meta.areaCode && findArea(parsed.meta.areaCode)
        ? parsed.meta.areaCode
        : 'rm-ui';
      const rawStatus = (statusFromLabels(props.issue.labels) ?? '').replace('status:', '');
      const status = (STATUSES as readonly string[]).includes(rawStatus)
        ? rawStatus as typeof STATUSES[number]
        : 'raised';
      return {
        title:            props.issue.title,
        category:         (CATEGORIES.includes(cat as typeof CATEGORIES[number]) ? cat : 'Bug') as typeof CATEGORIES[number],
        priority:         (PRIORITIES.includes(prio as typeof PRIORITIES[number]) ? prio : 'P3-Medium') as typeof PRIORITIES[number],
        area:             areaCode,
        subArea:          parsed.meta.subAreaCode,
        status,
        siteId:           parsed.meta.siteId,
        machineSerial:    parsed.meta.machineSerial,
        description:      parsed.description,
        stepsToReproduce: parsed.stepsToReproduce || undefined,
      };
    }
    return {
      title:            '',
      category:         'Bug',
      priority:         'P3-Medium',
      area:             'rm-ui',
      subArea:          undefined,
      status:           undefined,
      siteId:           undefined,
      machineSerial:    undefined,
      description:      '',
      stepsToReproduce: undefined,
    };
  // Stringify the issue to detect a different issue being passed; for
  // create mode the deps are static.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, mode === 'edit' ? JSON.stringify(props.issue) : '']);

  // Reset keep-map whenever the edit target changes / dialog opens.
  useEffect(() => {
    if (mode !== 'edit' || !open) {
      setKeepMap(new Map());
      return;
    }
    const parsed = parseIssueBody(props.issue.body);
    setKeepMap(new Map(parsed.attachments.map(a => [a.downloadUrl, true])));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, open, mode === 'edit' ? props.issue?.number : 0]);

  const editIssueBody = mode === 'edit' ? props.issue?.body : '';
  const existingAttachments = useMemo<ExistingAttachment[]>(() => {
    if (mode !== 'edit' || !editIssueBody) return [];
    return parseIssueBody(editIssueBody).attachments;
  }, [mode, editIssueBody]);

  const submit = async (values: IssueFormValues) => {
    const areaLabel    = findArea(values.area)?.label ?? values.area;
    const subAreaLabel = findSubArea(values.area, values.subArea)?.label;

    if (mode === 'create') {
      await create.mutateAsync({
        title:            values.title,
        category:         values.category,
        priority:         values.priority,
        area:             values.area,
        areaLabel,
        subArea:          values.subArea,
        subAreaLabel,
        siteId:           values.siteId,
        machineSerial:    values.machineSerial,
        description:      values.description,
        stepsToReproduce: values.stepsToReproduce,
        images:           files,
      });
    } else {
      const keepUrls = Array.from(keepMap.entries())
        .filter(([, keep]) => keep)
        .map(([url]) => url);
      await edit.mutateAsync({
        number:            props.issue.number,
        title:             values.title,
        category:          values.category,
        priority:          values.priority,
        area:              values.area,
        areaLabel,
        subArea:           values.subArea,
        subAreaLabel,
        status:            values.status,
        siteId:            values.siteId,
        machineSerial:     values.machineSerial,
        description:       values.description,
        stepsToReproduce:  values.stepsToReproduce,
        keepAttachmentUrls: keepUrls,
        images:            files,
      });
    }
    setFiles([]);
  };

  return (
    <EntityFormDialog<IssueFormValues>
      open={open}
      mode={mode}
      title={mode === 'create'
        ? 'Raise issue'
        : `Edit issue #${props.issue.number}`}
      submitLabel={mode === 'create' ? 'Create issue' : 'Save changes'}
      schema={schema}
      initialValues={initial}
      onSubmit={submit}
      onClose={() => { setFiles([]); onClose(); }}
    >
      <Fields
        files={files}
        setFiles={setFiles}
        existingAttachments={existingAttachments}
        keepMap={keepMap}
        setKeepMap={setKeepMap}
        mode={mode}
      />
    </EntityFormDialog>
  );
}

function titleCaseCategory(raw: string): string {
  if (!raw) return 'Bug';
  return raw[0].toUpperCase() + raw.slice(1).toLowerCase();
}

function formatPriority(raw: string): string {
  // Label form is "p3-medium"; UI wants "P3-Medium".
  return raw
    .split('-')
    .map(s => s.length === 0 ? '' : s[0].toUpperCase() + s.slice(1).toLowerCase())
    .join('-');
}

function Fields({
  files, setFiles,
  existingAttachments, keepMap, setKeepMap,
  mode,
}: {
  files: File[]; setFiles: (next: File[]) => void;
  existingAttachments: ExistingAttachment[];
  keepMap: Map<string, boolean>;
  setKeepMap: (next: Map<string, boolean>) => void;
  mode: 'create' | 'edit';
}) {
  const { register, control, setValue, formState: { errors } } = useFormContext<IssueFormValues>();
  // Watch the area selection so the sub-area select can re-render its
  // options + auto-clear when the parent area changes.
  const watchedArea = useWatch({ control, name: 'area' });
  const activeArea  = findArea(watchedArea);
  const subAreas    = activeArea?.subAreas ?? [];
  const me = useMe();
  const operatorIdForSites = me.data?.operator_id ?? null;
  const sites = useSitesList(operatorIdForSites);
  const sitesQ = useQuery({ queryKey: sites.queryKey, queryFn: sites.listFn });
  const alert = useAlert();
  const reporter = me.data?.display_name ?? me.data?.username ?? '—';

  const siteOptions = useMemo(() => selectableSites(sitesQ.data), [sitesQ.data]);

  const handleFilePick = (incoming: HTMLInputElement['files']) => {
    if (!incoming) return;
    const merged = [...files];
    const rejected: string[] = [];
    for (const f of Array.from(incoming)) {
      if (merged.length >= MAX_FILES) { rejected.push(`${f.name} (max ${MAX_FILES} files)`); continue; }
      if (f.size > MAX_BYTES) {
        rejected.push(`${f.name} (over ${MAX_BYTES / (1024 * 1024)} MB)`); continue;
      }
      if (!['image/jpeg', 'image/png', 'video/mp4'].includes(f.type)) {
        rejected.push(`${f.name} (unsupported type ${f.type || 'unknown'})`); continue;
      }
      merged.push(f);
    }
    setFiles(merged);
    if (rejected.length) {
      void alert({
        title: 'Some files were not attached',
        message: rejected.join('\n'),
        severity: 'warning',
      });
    }
  };

  return (
    <>
      <TextField
        label="Title"
        size="small"
        autoFocus
        helperText={errors.title?.message ?? 'A short one-line summary.'}
        error={!!errors.title}
        fullWidth
        {...register('title')}
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Controller
          name="category"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              select label="Category" size="small" sx={{ flex: 1 }}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              {...field}
            >
              {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
          )}
        />
        <Controller
          name="priority"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              select label="Priority" size="small" sx={{ flex: 1 }}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              {...field}
            >
              {PRIORITIES.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </TextField>
          )}
        />
      </Stack>

      {/* Component area picker (required) + cascading sub-area picker
          (optional, shown only when the selected area has sub-areas).
          Mirrors AppShell.tsx sections[] for the RM UI branch — see
          componentAreas.ts maintenance note. */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Controller
          name="area"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              select label="Component area" size="small" sx={{ flex: 1 }}
              error={!!fieldState.error}
              helperText={fieldState.error?.message ?? 'Which part of ARMS is this about?'}
              {...field}
              onChange={(e) => {
                field.onChange(e.target.value);
                // Always clear the sub-area when the parent area changes
                // — otherwise the form holds a sub-area code that no
                // longer exists in the new area's list.
                setValue('subArea', undefined as unknown as string, { shouldValidate: false });
              }}
            >
              {COMPONENT_AREAS.map(a => (
                <MenuItem key={a.code} value={a.code}>{a.label}</MenuItem>
              ))}
            </TextField>
          )}
        />
        {subAreas.length > 0 && (
          <Controller
            name="subArea"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                select label="Sub-area (optional)" size="small" sx={{ flex: 1 }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message
                  ?? 'Optional — narrow it down if you can.'}
                {...field}
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value === '' ? undefined : e.target.value)}
              >
                <MenuItem value="">Not sure / not listed</MenuItem>
                {subAreas.map(s => (
                  <MenuItem key={s.code} value={s.code}>{s.label}</MenuItem>
                ))}
              </TextField>
            )}
          />
        )}
      </Stack>

      {mode === 'edit' && (
        <Controller
          name="status"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              select label="Pipeline status" size="small" fullWidth
              error={!!fieldState.error}
              helperText={fieldState.error?.message
                ?? 'Pick any stage — moves are allowed in any direction.'}
              {...field}
              value={field.value ?? 'raised'}
            >
              {STATUSES.map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>
          )}
        />
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Controller
          name="siteId"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              select label="Site (optional)" size="small" sx={{ flex: 1 }}
              error={!!fieldState.error}
              helperText={fieldState.error?.message ?? 'Leave blank if not site-specific.'}
              {...field}
              value={field.value ?? ''}
              onChange={(e) => field.onChange(e.target.value === '' ? undefined : e.target.value)}
            >
              <MenuItem value="">Not site-specific</MenuItem>
              {siteOptions.map(s => (
                <MenuItem key={s.id} value={s.id}>
                  {s.displayName} ({s.code})
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        <TextField
          label="Machine serial (optional)"
          size="small"
          sx={{ flex: 1 }}
          helperText={errors.machineSerial?.message
            ?? 'Leave blank if not machine-specific.'}
          error={!!errors.machineSerial}
          {...register('machineSerial')}
        />
      </Stack>

      <TextField
        label="Description"
        size="small"
        multiline minRows={4}
        helperText={errors.description?.message
          ?? 'At least 20 characters. What did you do, and what happened?'}
        error={!!errors.description}
        fullWidth
        {...register('description')}
      />

      <TextField
        label="Steps to reproduce (optional)"
        size="small"
        multiline minRows={3}
        helperText={errors.stepsToReproduce?.message
          ?? 'Numbered steps help a lot.'}
        error={!!errors.stepsToReproduce}
        fullWidth
        {...register('stepsToReproduce')}
      />

      <TextField
        label="Reported by"
        size="small"
        value={reporter}
        disabled
        fullWidth
        helperText="Auto-filled from your account."
      />

      {mode === 'edit' && existingAttachments.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary"
            sx={{ display: 'block', mb: 0.5 }}>
            Existing attachments — click ✕ to remove on save.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {existingAttachments.map(a => {
              const kept = keepMap.get(a.downloadUrl) ?? true;
              return (
                <Chip
                  key={a.downloadUrl}
                  label={a.fileName}
                  variant={kept ? 'outlined' : 'filled'}
                  color={kept ? 'default' : 'error'}
                  size="small"
                  onClick={() => window.open(a.downloadUrl, '_blank', 'noopener,noreferrer')}
                  onDelete={() => {
                    const next = new Map(keepMap);
                    next.set(a.downloadUrl, !kept);
                    setKeepMap(next);
                  }}
                  deleteIcon={<CloseIcon />}
                  sx={{ textDecoration: kept ? 'none' : 'line-through' }}
                />
              );
            })}
          </Stack>
        </Box>
      )}

      <Box>
        <Button
          component="label"
          size="small"
          variant="outlined"
          startIcon={<AttachFileIcon fontSize="small" />}
        >
          Add attachment
          <input
            type="file"
            hidden
            multiple
            accept={ACCEPT}
            onChange={(e) => { handleFilePick(e.target.files); e.target.value = ''; }}
          />
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
          Up to {MAX_FILES} files; JPEG / PNG / MP4; 10 MB each.
        </Typography>
        {files.length > 0 && (
          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
            {files.map((f, i) => (
              <Chip
                key={`${f.name}-${i}`}
                label={`${f.name} · ${(f.size / 1024).toFixed(0)} KB`}
                onDelete={() => setFiles(files.filter((_, j) => j !== i))}
                deleteIcon={<CloseIcon />}
                variant="outlined"
                size="small"
              />
            ))}
          </Stack>
        )}
      </Box>
    </>
  );
}

